import { AddButton, FormSection, Select, Switch, TextField, Tooltip } from "components/Core"
import { Controller } from "react-hook-form"
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io"
import { item_type } from "./item.config"
import { Items } from "components/DinamicFormSections"
import { useEffect, useState } from "react"
import useSearchMiddlewareOSRM from "hooks/useSearchMiddlewareOSRM"
import Swal from "sweetalert2"
import BadgeAlertForm from "components/Core/Form/BadgeAlertForm"
import ValidationAlert from "components/Custom/ValidationAlert"
import { Env, MultiClient } from "config"
import { CLIENT } from "config/init"

interface IFormItem {
  control: any
  errors: any
  watch: any
  custom_field_form: any
  edit?: boolean
  isModal?: boolean
}

const FormItem = ({ control, errors, watch, custom_field_form, edit, isModal }: IFormItem) => {
  const [delivery_types, set_delivery_types] = useState<Array<string>>([])
  const [allExpanded, setAllExpanded] = useState<boolean | undefined>(isModal ? true : undefined)
  const { data: data_schedules, search_osrm_action: search_osrm_action_schedules } = useSearchMiddlewareOSRM({
    entity: "schedules",
  })
  const [_client] = MultiClient.get_user_env(null, Env.clients, Env.env, Env.defaults)
  const isEntelPeru = _client === CLIENT.ENTEL_PERU

  useEffect(() => {
    search_osrm_action_schedules({ from: 0, size: 1000, fields: ["id", "deliveryType"] })
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const get_delivery_types_action = async () => {
      try {
        let schedule_from_req = data_schedules?.records
        set_delivery_types(schedule_from_req?.map((sch: any) => sch?.deliveryType))
      } catch (error: any) {
        Swal.fire({
          title: "Error",
          text: JSON.stringify(error?.response?.data?.message),
          icon: "error",
        })
      }
    }
    get_delivery_types_action()
    // eslint-disable-next-line
  }, [data_schedules])

  const add_new_custom = () => custom_field_form.append({ type: "string", key: "", value: "" })

  const validationsSectionsErrors = {
    1: Boolean(["name", "sku", "storePickup", "enabled"].some((key: string) => Object.keys(errors).includes(key))),
    2: Boolean(["categories", "barcodes"].some((key: string) => Object.keys(errors).includes(key))),
    3: Boolean(["logisticAttributes"].some((key: string) => Object.keys(errors).includes(key))),
    4: Boolean(["preparationTime"].some((key: string) => Object.keys(errors).includes(key))),
    5: Boolean(["custom"].some((key: string) => Object.keys(errors).includes(key))),
  }

  const toggleAll = () => setAllExpanded((prev) => (prev === undefined ? false : !prev))

  return (
    <div className="w-full h-full flex flex-col items-center justify-start bg-[#F2F2F7] overflow-auto">
      {/* Global Toggle Sections */}
      <div className="w-full lg:w-3/5 flex justify-end mb-2 pr-2">
        <Tooltip content={allExpanded ? "Cerrar todos los bloques" : "Abrir todos los bloques"} align="top">
          <button
            type="button"
            onClick={toggleAll}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md transition-all text-[13px] font-bold text-gray-500 hover:text-gray-800"
          >
            {allExpanded ? (
              <>
                <IoIosArrowUp className="text-lg" />
                Colapsar todo
              </>
            ) : (
              <>
                <IoIosArrowDown className="text-lg" />
                Expandir todo
              </>
            )}
          </button>
        </Tooltip>
      </div>

      <FormSection
        title="Información general"
        helperText="Información general"
        number={1}
        anyError={validationsSectionsErrors[1]}
        closed={allExpanded === undefined ? false : !allExpanded}
        className={isModal ? "w-full lg:!w-full" : undefined}
      >
        <div className="w-full flex flex-col gap-4 lg:px-16 lg:py-10">
          <Controller
            name="sku"
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <TextField label="SKU" onChange={onChange} value={value} disabled={Boolean(edit)} />
            )}
          />
          <Controller
            name="name"
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => <TextField label="Nombre" onChange={onChange} value={value} />}
          />
          <div className="w-full flex flex-col lg:flex-row gap-4">
            {isEntelPeru ? null : (
              <Controller
                name="safety"
                control={control}
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <TextField label="Stock de seguridad" type="number" onChange={onChange} value={value} />
                )}
              />
            )}
            <Controller
              name="type"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select options={item_type} label="Tipo" onChange={onChange} value={value} />
              )}
            />
          </div>
          <div className="flex flex-col lg:flex-row gap-4">
            <Controller
              name="storePickup"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Switch label="Retiro en tienda" onChange={onChange} checked={value} />
              )}
            />
            <Controller
              name="enabled"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Switch label="Artículo habilitado" onChange={onChange} checked={value} />
              )}
            />
          </div>
          {Boolean(validationsSectionsErrors[1]) ? (
            <BadgeAlertForm>
              <ValidationAlert
                errors={{
                  sku: errors?.sku,
                  name: errors?.name,
                  safety: errors?.safety,
                  type: errors?.type,
                  storePickup: errors?.storePickup,
                  enabled: errors?.enabled,
                }}
                sectionName="Información general"
              />
            </BadgeAlertForm>
          ) : null}
        </div>
      </FormSection>
      <FormSection
        title="Detalle de artículo"
        helperText="Detalle de artículo"
        number={2}
        anyError={validationsSectionsErrors[2]}
        closed={allExpanded === undefined ? true : !allExpanded}
        className={isModal ? "w-full lg:!w-full" : undefined}
      >
        <div className="w-full flex flex-col gap-4 lg:px-16 lg:py-10">
          <Controller
            name="categories"
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <TextField label="Categorías" onChange={onChange} value={value} />
            )}
          />
          <Controller
            name="barcodes"
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <TextField label="Código de barras" onChange={onChange} value={value} />
            )}
          />
          {Boolean(validationsSectionsErrors[2]) ? (
            <BadgeAlertForm>
              <ValidationAlert
                errors={{
                  categories: errors?.categories,
                  barcodes: errors?.barcodes,
                }}
                sectionName="Detalle de artículo"
              />
            </BadgeAlertForm>
          ) : null}
        </div>
      </FormSection>
      <FormSection
        title="Atributos logísticos"
        helperText="Atributos logísticos"
        number={3}
        anyError={validationsSectionsErrors[3]}
        closed={allExpanded === undefined ? true : !allExpanded}
        className={isModal ? "w-full lg:!w-full" : undefined}
      >
        <div className="w-full flex flex-col gap-4 lg:px-16 lg:py-10">
          <Controller
            name="logisticAttributes.size"
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => <TextField label="Tamaño" onChange={onChange} value={value} />}
          />
          <Controller
            name="logisticAttributes.volume"
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <TextField label="Volumen" type="number" onChange={onChange} value={value} />
            )}
          />
          {Boolean(validationsSectionsErrors[3]) ? (
            <BadgeAlertForm>
              <ValidationAlert
                errors={{
                  logisticAttributes: errors?.logisticAttributes,
                }}
                sectionName="Atributos logísticos"
              />
            </BadgeAlertForm>
          ) : null}
        </div>
      </FormSection>
      <FormSection
        title="Tiempo de preparación"
        helperText="Tiempo de preparación"
        number={4}
        anyError={validationsSectionsErrors[4]}
        closed={allExpanded === undefined ? true : !allExpanded}
        className={isModal ? "w-full lg:!w-full" : undefined}
      >
        <div className="w-full flex flex-col gap-4 lg:px-16 lg:py-10">
          {Boolean(delivery_types?.length) ? (
            delivery_types?.map((del: string, _i: number) => (
              <Controller
                key={_i}
                name={`preparationTime[${del}]`}
                control={control}
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <TextField label={del} type="number" onChange={onChange} value={value} />
                )}
              />
            ))
          ) : (
            <Controller
              name="preparationTime.default"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <TextField label="Default" type="number" onChange={onChange} value={value} />
              )}
            />
          )}
          {Boolean(validationsSectionsErrors[4]) ? (
            <BadgeAlertForm>
              <ValidationAlert
                errors={{
                  preparationTime: errors?.preparationTime,
                }}
                sectionName="Tiempo de preparación"
              />
            </BadgeAlertForm>
          ) : null}
        </div>
      </FormSection>
      <FormSection
        title="Custom"
        helperText="Custom"
        number={5}
        anyError={validationsSectionsErrors[5]}
        closed={allExpanded === undefined ? true : !allExpanded}
        className={isModal ? "w-full lg:!w-full" : undefined}
      >
        <div className="w-full flex flex-col gap-4 lg:px-16 lg:py-10">
          {custom_field_form.fields.map((customItem: any, i: number) => {
            return (
              <Items.CustomFields
                key={customItem.id}
                index={i}
                custom={custom_field_form}
                control={control}
                errors={errors}
                watch={watch}
              />
            )
          })}
          <div className="flex flex-row items-center justify-start">
            <AddButton onClick={add_new_custom} />
          </div>
          {Boolean(errors?.custom) ? (
            <BadgeAlertForm>
              <ValidationAlert errors={errors?.custom} sectionName="Custom" />
            </BadgeAlertForm>
          ) : null}
        </div>
      </FormSection>
    </div>
  )
}

export default FormItem
